import {Injectable, Injector} from '@angular/core';
import {Store} from '@ngrx/store';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {IReferenceItem} from '@ispm/core/interfaces/reference.interface';
import {EMRIntegrationBeacon} from '@ispm-client/modules/pathways';
import {EmrIntegrationArguments} from '@ispm/core/interfaces/emr-integration-arguments';
import {Observable} from 'rxjs';
import {ISearchSource, SearchSource} from '@ispm/aberrations/interfaces/annotation-searchable-links.interface';
import {InterpolateService} from '@ispm/shared/services/interpolate.service';
import {StoreActionCreatorService} from '@ispm/shared/services/store-action-creator.service';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {get} from 'lodash';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {IWorkflow} from '@ispm/core/interfaces/workflow.interface';
import {WebMessagingService} from '@ispm/core/services/web-messaging.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import { ActivatedRoute } from '@angular/router';

@Injectable()
export class EmrIntegrationService {

  get emrIntegration(): IReferenceItem {
    return this._emrIntegration;
  }

  set emrIntegration(value: IReferenceItem) {
    this._emrIntegration = value;
  }

  get currentTest(): IDiagnosticOrder {
    return this._currentTest;
  }

  set currentTest(value: IDiagnosticOrder) {
    this._currentTest = value;
  }

  get currentWorkflow(): IWorkflow {
    return this._currentWorkflow;
  }

  set currentWorkflow(value: IWorkflow) {
    this._currentWorkflow = value;
  }

  constructor(
    private store$: Store<IgenomicsState>,
    private injector: Injector,
    private interpolateService: InterpolateService,
    private storeService: StoreActionCreatorService,
    private workflowConfigService: WorkflowConfigService,
    private routerManagerService: RouterManagerService,
    public activatedRoute: ActivatedRoute) {
  }

  private _emrIntegration: IReferenceItem;
  private _currentTest: IDiagnosticOrder;
  private _currentWorkflow: IWorkflow;
  private selectProtocolFromPathwaysMessage: string = 'SelectProtocolFromPathways';
  private selectProtocolFromPathwaysArgsConfig: object;

  private getWebMessages(): WebMessagingService {
    return this.injector.get(WebMessagingService);
  }

  public redirectToEMRIntegration(emrIntegration: IReferenceItem, currentTest?, currentWorkflow?): string {
    this.emrIntegration = emrIntegration;
    this.currentTest = (!!currentTest) ? currentTest : this.currentTest;
    this.currentWorkflow = (!!currentWorkflow) ? currentWorkflow : this.currentWorkflow;
    let valueToCopy: string = '';
    const copyToClipboardType = 'copyToClipboard';
    const webMessagesType = 'webMessages';
    const enableEMRIntegrationFromUrl = this.activatedRoute?.snapshot?.queryParams?.EMRIntegration === EMRIntegrationBeacon;
    const emrIntegrationArgs: EmrIntegrationArguments = this.emrIntegration && this.emrIntegration.args ? JSON.parse(this.emrIntegration.args) : undefined;
    if (emrIntegrationArgs !== undefined) {
      if (enableEMRIntegrationFromUrl || this.emrIntegration.type == webMessagesType) {
        if (this.emrIntegration.type == copyToClipboardType) {
          this.generateEMRIntegrationString(emrIntegrationArgs.searchSources, this.emrIntegration.value, {
            cacheKey: this.workflowConfigService.getCacheKeySync()
          }).filter(integrationString => !!integrationString).subscribe(integrationString => {
            valueToCopy = integrationString;
          });
        } else {
          this.checkEMRIntegrationConditions(emrIntegrationArgs.conditions).take(1).subscribe(enabled => {
            if (enabled) {
              this.generateEMRIntegrationString(emrIntegrationArgs.searchSources, this.emrIntegration.value, {
                cacheKey: this.workflowConfigService.getCacheKeySync()
              }).take(1).filter(integrationString => !!integrationString).subscribe(integrationString => {
                if (this.emrIntegration.type == webMessagesType) {
                  this.getWebMessages().isAvailable().subscribe((res) => {
                    this.selectProtocolFromPathwaysArgsConfig = this.getWebMessages().getSelectProtocolFromPathwaysArgs();
                    this.selectProtocolFromPathwaysArgsConfig['protocolId'] = integrationString;
                    if (res) {
                      console.log('Send web message to EMR Integration with args: ' + this.selectProtocolFromPathwaysArgsConfig['protocolId'] + ', ' + this.selectProtocolFromPathwaysArgsConfig['protocolIdType'] );
                    } else {
                      console.log('Web messages is not available');
                    }
                    this.getWebMessages().postMessageByConfig(this.selectProtocolFromPathwaysMessage, this.selectProtocolFromPathwaysArgsConfig);
                  });
                } else {
                  console.log('Redirect to EMR Integration url: ' + integrationString);
                  const windowReference = window.open(integrationString, '_blank');
                  windowReference.location.href = integrationString;
                  windowReference.focus();
                }
                this.resetEMRIntegrationModule();
              });
            }
          });
      }
    }
  }
    return valueToCopy;
  }

  private resetEMRIntegrationModule() {
    this.emrIntegration = null;
    this.currentWorkflow = null;
    this.currentTest = null;
  }

  getEMRIntegration() {
    if (this.routerManagerService.queryParameters?.EMRIntegration) {
      return 'EMRIntegration=' + this.routerManagerService.queryParameters.EMRIntegration;
    }
    return null;
  }

  getEMRIntegrationObj() {
    if (this.routerManagerService.queryParameters?.EMRIntegration) {
      return {'EMRIntegration': this.routerManagerService.queryParameters.EMRIntegration};
    }
    return {};
  }

  private checkEMRIntegrationConditions(conditions: Array<ISearchSource>) {

    const observSources = this.extractFieldFromSources(conditions);
    return Observable.forkJoin(observSources)
      .map((values: { fieldFromSource: any, searchSource: ISearchSource }[]) => {
        return values.find((v) => v.fieldFromSource !== v.searchSource.staticValue) === undefined;
      });
  }

  private getSearchSources(source: string): any {
    switch (source) {
      case SearchSource.STORE_AC_SERVICE:
        return this.store$;
      case SearchSource.DIAGNOSTIC_ORDER:
        return Observable.of(this.currentTest);
      case SearchSource.CURRENT_WORKFLOW:
        return Observable.of(this.currentWorkflow);
    }
  }

  private extractFieldFromSources(searchSources: Array<ISearchSource>, args?): Array<Observable<any>> {
    const observSources = [];

    searchSources.forEach(searchSource => {
      if (searchSource.sourceKey) {
        observSources.push(this.extractFieldFromSource(searchSource, args).take(1));
      }
    });
    return observSources;
  }

  private generateEMRIntegrationString(searchSources: Array<ISearchSource>, emrURLTemplate: string, args: any): Observable<string> {

    const observSources = this.extractFieldFromSources(searchSources, args);

    return Observable.forkJoin(observSources)
      .map((values: { fieldFromSource: any, searchSource: ISearchSource }[]) => {
        const objForInterpolation = { };
        values.forEach(v => objForInterpolation[v.searchSource.key] = v.fieldFromSource);
        const interpolationResult = this.interpolateService.interpolate(emrURLTemplate, objForInterpolation, true,
          { useEmptyValues: false, usePartialPath: false});

        if (InterpolateService.hasInterpolationString(interpolationResult)) {
          return null;
        } else {
          return interpolationResult;
        }
      });
  }

  private extractFieldFromSource(searchSource: ISearchSource, args?): Observable<any> {
    const sourceLocation = this.getSearchSources(<string> searchSource.source);
    const interpolatedField = this.interpolateService.interpolate(
      searchSource.sourceKey,
      args,
      true,
      { useEmptyValues: false, usePartialPath: false}
    );

    return this.storeService.getObsValue<any>(interpolatedField.split('.'), sourceLocation).map(value => {
      const fieldFromSource = searchSource.field ? get(value, searchSource.field, null) : value;
      return { fieldFromSource, searchSource};
    });
  }
}


